import InvoiceType from "@/enums/invoiceType";

export default class CalculationsHelper {
    static round(amount, precision = 2) {
        if (amount === null || amount === undefined) return 0;
        return Number(parseFloat(amount).toFixed(precision));
    }

    static getType(invoice) {
        return invoice.type;
    }

    static getAmount(invoice) {
        const amount = invoice.scoring?.amount ?? invoice.amount;
        return this.round(amount);
    }

    static getAequitexFeeSellerPercentage(invoice) {
        return this.round(invoice.scoring?.fee_hive) ?? 0;
    }

    static getAequitexFeeSellerAmount(invoice) {
        return this.round(this.getAequitexFeeSellerPercentage(invoice) * this.getAmount(invoice) / 100);
    }

    static getAequitexFeeInvestorPercentage(invoice) {
        return this.round(invoice.scoring?.investor_fee) ?? 0;
    }

    static getAequitexFeeInvestorAmount(invoice) {
        return this.round(this.getAequitexFeeInvestorPercentage(invoice) * this.getAmount(invoice) / 100);
    }

    static getDiscountPercentage(invoice) {
        return this.round(invoice.scoring?.discount_in_percentage) ?? 0;
    }

    static getDiscountAmount(invoice) {
        return this.round(this.getAmount(invoice) * this.getDiscountPercentage(invoice) / 100);
    }

    static getRefundPercentage(invoice) {
        return invoice.scoring?.refund_percentage ?? 0;
    }

    static getRefundAmount(invoice) {
        return this.round(this.getAmount(invoice) * this.getRefundPercentage(invoice) / 100);
    }

    static getRefundDefaultPercentage(invoice) {
        return invoice.scoring?.refund_if_defaults ?? 0;
    }

    static getRefundDefaultAmount(invoice) {
        return this.round(this.getAmount(invoice) * this.getRefundDefaultPercentage(invoice) / 100);
    }

    static getRoiPercentage(invoice) {
        switch (invoice.type) {
              case InvoiceType.NonRecourse:
              case InvoiceType.Recourse:
                return this.round(
                    this.getDiscountPercentage(invoice)
                    - this.getAequitexFeeSellerPercentage(invoice)
                    - this.getAequitexFeeInvestorPercentage(invoice)
                );
              case InvoiceType.Reverse:
                return this.round(this.getRoiAmount(invoice) / this.getAmount(invoice) * 100);
            default:
                return null;
        }
    }

    static getRoiAmount(invoice) {
        switch (invoice.type) {
              case InvoiceType.NonRecourse:
              case InvoiceType.Recourse:
                return this.round(this.getAmount(invoice) * this.getRoiPercentage(invoice) / 100);
              case InvoiceType.Reverse:
                return this.round(
                    this.getDiscountAmount(invoice)
                    - this.getAequitexFeeInvestorAmount(invoice)
                    - this.getAequitexFeeSellerAmount(invoice)
                );
            default:
                return null;
        }
    }

    static getInvestorRoiAmount(invoice) {
        const aequitexFeeInvestor = this.getAequitexFeeInvestorAmount(invoice);
        switch (invoice.type) {
              case InvoiceType.NonRecourse:
              case InvoiceType.Recourse:
                return this.round(this.getRoiAmount(invoice) - aequitexFeeInvestor);
              case InvoiceType.Reverse:
                return this.round(this.getDiscountAmount(invoice) - aequitexFeeInvestor);
            default:
                return null;
        }
    }

    static getInvestorRoiPercentage(invoice) {
        const investorRoiAmount = this.getInvestorRoiAmount(invoice);
        const investmentAmount = this.getInvestmentAmount(invoice);
        return this.round(investorRoiAmount / investmentAmount * 100);
    }

    static getInvestmentAmount(invoice) {
        switch (invoice.type) {
              case InvoiceType.NonRecourse:
              case InvoiceType.Recourse:
                return this.round(this.getAmount(invoice) - this.getInvestorRoiAmount(invoice));
              case InvoiceType.Reverse:
                return this.round(this.getAmount(invoice) + this.getAequitexFeeInvestorAmount(invoice));
            default:
                return null;
        }
    }

    static getLiquidityAmount(invoice) {
        switch (invoice.type) {
              case InvoiceType.NonRecourse:
              case InvoiceType.Recourse:
                return this.round(
                    this.getAmount(invoice)
                    - this.getDiscountAmount(invoice)
                    + this.getAequitexFeeInvestorAmount(invoice)
                    - this.getRefundAmount(invoice)
                    - this.getRefundDefaultAmount(invoice)
                );
              case InvoiceType.Reverse:
                return this.round(
                    this.getAmount(invoice)
                    - this.getRefundAmount(invoice)
                    - this.getRefundDefaultAmount(invoice)
                    - this.getAequitexFeeSellerAmount(invoice)
                );
            default:
                return null;
        }
    }

    static getInvestorRepaymentAmount(invoice) {
        return this.round(this.getAmount(invoice) + this.getDiscountAmount(invoice));
    }

    static getInvestorSettledAmount(invoice, fraction) {
        switch (invoice.type) {
              case InvoiceType.NonRecourse:
              case InvoiceType.Recourse:
                return this.round(fraction * this.getAmount(invoice) / 100);
              case InvoiceType.Reverse:
                return this.round(fraction * (this.getAmount(invoice) + this.getDiscountAmount(invoice)) / 100);
            default:
                return null;
        }
    }

    static getInvestorVoluntaryAmount(invoice, fraction) {
        const fractionRefund = (this.getRefundAmount(invoice) * fraction / 100);
        const currentDate = new Date();
        const dueDate = new Date(invoice.due_date);

        const dueDatePlus7Days = new Date(dueDate.getTime() + (7 * 24 * 60 * 60 * 1000));

        let refund;
        if (currentDate > dueDatePlus7Days) {
            refund = fractionRefund / 2;
        } else {
            refund = fractionRefund / 3;
        }

        return this.round(refund);
    }

    static getInvestorDefaultVoluntaryPercent(invoice) {
        return this.round(invoice.scoring?.investor_voluntary_compensation ?? 0);
    }

    static getInvestorDefaultVoluntaryAmount(invoice) {
        return this.round(this.getInvestorDefaultVoluntaryPercent(invoice) * this.getRefundDefaultAmount(invoice) / 100);
    }

    static getInvestorDefaultAmount(invoice, fraction) {
        return this.round(fraction * this.getInvestorDefaultVoluntaryAmount(invoice) / 100);
    }

    static getSellerSettledAmount(invoice) {
        return this.round(this.getRefundAmount(invoice) + this.getRefundDefaultAmount(invoice));
    }

    static getRepaymentAmount(invoice)
    {
        return this.round(this.getAmount(invoice) + this.getDiscountAmount(invoice));
    }
}
